import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useBooking } from '../hooks/useBooking';

const BASE_STEPS = [
  { number: 1, label: 'Service', path: '/book' },
  { number: 2, label: 'Date & Time', path: '/book/date-time' },
  { number: 3, label: 'Your Info', path: '/book/details' },
  { number: 4, label: 'Confirm', path: '/book/confirm' },
];

const STEPS_WITH_FORMS = [
  { number: 1, label: 'Service', path: '/book' },
  { number: 2, label: 'Date & Time', path: '/book/date-time' },
  { number: 3, label: 'Your Info', path: '/book/details' },
  { number: 4, label: 'Forms', path: '/book/forms' },
  { number: 5, label: 'Confirm', path: '/book/confirm' },
];

interface BookingStepperProps {
  currentStep: number;
  hasFormsStep?: boolean;
}

export function BookingStepper({ currentStep, hasFormsStep = false }: BookingStepperProps) {
  const navigate = useNavigate();
  const { businessListing } = useBooking();

  const steps = hasFormsStep ? STEPS_WITH_FORMS : BASE_STEPS;

  const handleStepClick = (step: typeof steps[number]) => {
    if (step.number < currentStep) {
      navigate(step.path);
    }
  };

  return (
    <div className="bg-site-bg border-b border-site-border sticky top-0 z-20">
      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Business name — links back to homepage */}
        <Link to="/" className="flex items-center gap-3 mb-5 no-underline hover:opacity-80 transition-opacity">
          {businessListing?.logo_url && (
            <img
              src={businessListing.logo_url}
              alt={businessListing.business_name}
              className="h-8 object-contain"
            />
          )}
          <h1 className="text-lg font-bold text-site-text">
            {businessListing?.business_name}
          </h1>
        </Link>

        {/* Stepper — circles row with lines behind */}
        <div className="relative flex items-center justify-between">
          {/* Connector lines layer (sits behind circles) */}
          <div className="absolute top-[18px] left-0 right-0 flex items-center px-[18px]">
            {steps.slice(1).map((step, i) => {
              const segmentDone = step.number <= currentStep;
              return (
                <div
                  key={`line-${i}`}
                  className={`flex-1 h-[3px] rounded-full ${segmentDone ? 'bg-site-primary' : 'bg-site-border'}`}
                />
              );
            })}
          </div>

          {/* Step circles + labels */}
          {steps.map((step) => {
            const isCurrent = step.number === currentStep;
            const isFuture = step.number > currentStep;
            const isClickable = step.number < currentStep;

            // Done and current steps are filled; future steps are outlined with muted numbers
            const circleColors = isFuture
              ? 'bg-site-bg text-site-muted border-2 border-site-border'
              : 'bg-site-primary text-site-on-primary border border-site-primary-outline';

            return (
              <div key={step.number} className="flex flex-col items-center z-10">
                {/* Backing circle so the connector line doesn't show through */}
                <div className="rounded-full bg-site-bg">
                  <button
                    onClick={() => handleStepClick(step)}
                    disabled={!isClickable}
                    className={`
                      w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold
                      transition-all ${circleColors}
                      ${isClickable ? 'cursor-pointer hover:scale-110' : 'cursor-default'}
                    `}
                    aria-label={`Step ${step.number}: ${step.label}${isClickable ? ' (click to go back)' : ''}`}
                  >
                    {step.number}
                  </button>
                </div>
                <span
                  className={`
                    text-xs mt-1.5 whitespace-nowrap
                    ${isCurrent ? 'font-bold text-site-text' : isFuture ? 'font-medium text-site-muted' : 'font-medium text-site-muted'}
                  `}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
